import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { trackUsabilityTest } from 'app/utils/statistics/customEvents';
import { trackCustomEvent } from 'app/utils/statistics/clientStatistics';
import { Survey } from 'app/graphql/__generated__/types/contento';
import {
    StyledSurvey,
    StyledSurveyTextContainer,
    FixedContainer,
    OngoingTestIndicator,
    StyledSurveyImage,
} from './UsabilityTest.styles';
import Intro from './Intro/Intro';
import Info from './Info/Info';
import Expected from './Expected/Expected';
import Task from './Task/Task';
import Experienced from './Experienced/Experienced';
import { USER_EVENT_TYPE } from '../../utils/statistics/customEvents';

type Props = {
    survey: Survey; // Add Contento type here when its available
    onCloseSurvey: (id: string) => void;
    onTakeSurvey: (id: string) => void;
    url: string;
};

export enum USER_TEST_STEP {
    INTRO = 'intro',
    INFO = 'info',
    EXPECTED = 'expected',
    TASK = 'task',
    EXPERIENCED = 'experienced',
}

const UsabilityTest = ({ survey, onCloseSurvey, url, onTakeSurvey }: Props): JSX.Element => {
    const [currentStep, setCurrentStep] = useState<USER_TEST_STEP>(USER_TEST_STEP.INTRO);
    const [selectedTask, setSelectedTask] = useState(null); // ts stuff todo

    const handleUserTestIntroStep = (): void => {
        const userEvent = {
            type: USER_EVENT_TYPE.ACCEPTED,
            task: selectedTask.task,
        };
        trackCustomEvent(trackUsabilityTest(userEvent));
        setCurrentStep(USER_TEST_STEP.INFO);
    };

    const handleUserTestInfoStep = (): void => {
        setCurrentStep(USER_TEST_STEP.EXPECTED);
    };

    const handleOnClose = (id: string) => {
        const userEvent = {
            type: USER_EVENT_TYPE.CANCEL,
            task: selectedTask.task,
        };
        trackCustomEvent(trackUsabilityTest(userEvent));
        onCloseSurvey(id);
    };

    const handleUserTestExpectedStep = (value: number): void => {
        const userEvent = {
            type: USER_EVENT_TYPE.START,
            value,
            task: selectedTask.task,
        };
        trackCustomEvent(trackUsabilityTest(userEvent));
        setCurrentStep(USER_TEST_STEP.TASK);
    };

    const handleUserTestTaskStep = (): void => {
        const userEvent = {
            type: USER_EVENT_TYPE.DONE,
            task: selectedTask.task,
        };
        trackCustomEvent(trackUsabilityTest(userEvent));
        setCurrentStep(USER_TEST_STEP.EXPERIENCED);
    };

    const handleUserTestExperiencedStep = (value: number): void => {
        const userEvent = {
            type: USER_EVENT_TYPE.EXPERIENCED,
            task: selectedTask.task,
            value,
        };
        trackCustomEvent(trackUsabilityTest(userEvent));
        onTakeSurvey(survey.id);
    };

    const isFrontPage = url === '/';

    useEffect(() => {
        const tasks = survey.customAttributes.find((item) => item.key === 'tasks').value;
        let parsedTasks = [];
        if (tasks) {
            try {
                parsedTasks = JSON.parse(tasks) || [];
            } catch (e) {
                console.log('error parsing tasks', e); // eslint-disable-line
                parsedTasks = [];
            }
        }
        const randomTask = Math.floor(Math.random() * parsedTasks.length);
        setSelectedTask(parsedTasks[randomTask]);
    }, []);

    const getSubPage = (): JSX.Element => {
        switch (currentStep) {
            case USER_TEST_STEP.INTRO:
                return (
                    <Intro onClick={handleUserTestIntroStep} survey={survey} onCloseSurvey={handleOnClose} />
                );
            case USER_TEST_STEP.INFO:
                return (
                    <Info onClick={handleUserTestInfoStep} survey={survey} onCloseSurvey={handleOnClose} />
                );
            case USER_TEST_STEP.EXPECTED:
                return (
                    <Expected
                        onClick={handleUserTestExpectedStep}
                        survey={survey}
                        onCloseSurvey={handleOnClose}
                        task={selectedTask}
                    />
                );
            case USER_TEST_STEP.TASK:
                return (
                    <Task
                        onClick={handleUserTestTaskStep}
                        survey={survey}
                        onCloseSurvey={handleOnClose}
                        task={selectedTask}
                    />
                );
            case USER_TEST_STEP.EXPERIENCED:
                return (
                    <Experienced
                        onClick={handleUserTestExperiencedStep}
                        survey={survey}
                        onCloseSurvey={handleOnClose}
                    />
                );
            default:
                return null;
        }
    };

    const OngoingTestBorder = (step) => {
        // eslint-disable-next-line react/destructuring-assignment
        if (USER_TEST_STEP.TASK !== step.currentStep) {
            return null;
        }

        return ReactDOM.createPortal(<OngoingTestIndicator />, document.getElementById('__next'));
    };

    const isIntroStep = currentStep === USER_TEST_STEP.INTRO;
    if (!isFrontPage && isIntroStep) {
        return null;
    }

    return (
        <FixedContainer>
            <OngoingTestBorder currentStep={currentStep} />
            <StyledSurvey>
                <StyledSurveyTextContainer>{getSubPage()}</StyledSurveyTextContainer>
                {isIntroStep && <StyledSurveyImage />}
            </StyledSurvey>
        </FixedContainer>
    );
};

export default UsabilityTest;
